import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { setCurrentBounds, startLeadFinderSearch } from "@app/scene/map/store/leadFinder/actions";
import {
  getLeadFinderCurrentBounds,
  getLeadFinderMode,
  getLeadFinderSearch,
} from "@app/scene/map/store/leadFinder/selectors";
import { getMapViewTool } from "@app/scene/map/store/mapMode/selectors";
import { isCurrentUserOwner, isLeadFinderPro } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import showLeadFinderLimitAlert from "@app/util/leadFinder/showLeadFinderLimitAlert";

import styles from "./MoreResults.module.scss";

const boundsChangeOptions = { maxWait: 500 };

interface Props {
  available: boolean;
  currentBounds?: google.maps.LatLngBounds;
  isOwner: boolean;
  mode: AreaSearchMode;
  onUpdate?: () => void;
  search?: string;
  setCurrentBounds: typeof setCurrentBounds;
  startLeadFinderSearch: typeof startLeadFinderSearch.request;
  tool?: MapTool;
}

const MoreResults: React.FC<Props> = ({
  available,
  currentBounds,
  isOwner,
  mode,
  onUpdate,
  search,
  setCurrentBounds,
  startLeadFinderSearch,
  tool,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { map } = useMap();
  const [visible, show, hide] = useBoolean(false);

  useEffect(() => {
    const bounds = map?.getBounds();
    if (bounds) {
      setCurrentBounds({ bounds });
      hide();
    }
  }, [map, mode, hide, setCurrentBounds]);

  useMapEventHandler(
    map,
    // We don't use `idle` because it is not triggered if you stopped moving but keep mouse
    // button pressed. Hence, we use debounce to avoid calling backend too frequently.
    // However, we still use maxWait to guarantee that at least one request is sent every
    // maxWait milliseconds. This is needed to load data if user is panning continuously.
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        if (available && search && currentBounds) {
          if (this.getBounds()?.equals(currentBounds)) {
            hide();
          } else {
            show();
          }
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  const onSearch = useCallback(
    (search: string) => {
      hide();
      startLeadFinderSearch({
        callback: () => {
          const bounds = map?.getBounds();
          if (bounds) {
            setCurrentBounds({ bounds });
          }
        },
        refresh: true,
        search,
      });

      onUpdate?.();
    },
    [hide, map, onUpdate, setCurrentBounds, startLeadFinderSearch]
  );

  const handleClick = useCallback(() => {
    analytics.clicked(["Get more results in this area"]);

    if (search) {
      startLeadFinderSearch({
        callback: () => {
          hide();
          const bounds = map?.getBounds();
          if (bounds) {
            setCurrentBounds({ bounds });
          }
          onUpdate?.();
        },
        onLimitThresholdReached: (limits) => {
          showLeadFinderLimitAlert(intl, limits, isOwner, onSearch, search);
        },
        refresh: true,
        search,
      });
    }
  }, [
    analytics,
    hide,
    intl,
    isOwner,
    map,
    onSearch,
    onUpdate,
    search,
    setCurrentBounds,
    startLeadFinderSearch,
  ]);

  const isVisible = available && visible && map && search && tool !== MapTool.LASSO;

  return isVisible ? (
    <div className={styles.container}>
      <Interactive>
        <Button className={styles.btn} onClick={handleClick} size="large" type="pill">
          {intl.formatMessage({
            id: "map.tool.leadFinder.moreResults.title",
            defaultMessage: "Get more results in this area",
            description: "Get more results in this area - button title",
          })}
        </Button>
      </Interactive>
    </div>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  available: isLeadFinderPro(state),
  currentBounds: getLeadFinderCurrentBounds(state),
  isOwner: isCurrentUserOwner(state),
  mode: getLeadFinderMode(state),
  search: getLeadFinderSearch(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  setCurrentBounds,
  startLeadFinderSearch: startLeadFinderSearch.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MoreResults);
